import React, { useState, useEffect, useMemo, useRef } from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import TextField from "@mui/material/TextField";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Autocomplete from "@mui/material/Autocomplete";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";

import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import PaletteIcon from "@mui/icons-material/Palette";
import CategoryIcon from "@mui/icons-material/Category";
import TuneIcon from "@mui/icons-material/Tune";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";

import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import "@blocknote/core/fonts/inter.css";
import "@blocknote/mantine/style.css";
import { buildPreviewSchema } from "./trainingBlockSpecs";
import { COLOR_OPTIONS } from "./colorOptions";

import { API_get_learning_section_detail } from "../../../constants";
import { getRightworkAPI } from "../requests/utilities/requests";
import SectionInputsModal2 from "./SectionInputsModal2";
import DeleteWarningModal from "./DeleteWarningModal";
import LoadSpinner from "../../../utilities/LoadSpinner";
import CustomCategorySelect from "./CustomCategorySelect";
import TestOutSkipsNote from "./TestOutSkipsNote";
import DirectAssignabilityToggle, {
  setDirectAssignability,
} from "./DirectAssignabilityToggle";
import SectionPreview from "./SectionPreview";
import { showUndoToast } from "./showUndoToast";
import { toast } from "react-toastify";
import LearningContentPreviewModal from "./LearningContentPreviewModal";
import LearningPreviewMenuButton from "./LearningPreviewMenuButton";

const checkboxIcon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkboxCheckedIcon = <CheckBoxIcon fontSize="small" />;

const modalStyle = {
  // Center without `transform` — @hello-pangea/dnd's drag clone uses
  // `position: fixed`, which becomes relative to the nearest transformed
  // ancestor instead of the viewport. With a transformed modal wrapping a
  // scrollable drag list, dragging offsets the ghost by the scroll amount.
  // `inset: 0 + margin: auto` centers identically without a transform.
  position: "absolute",
  inset: 0,
  margin: "auto",
  width: "96%",
  height: "94%",
  maxWidth: "1600px",
  maxHeight: "94vh",
  bgcolor: "background.paper",
  borderRadius: "12px",
  boxShadow: 24,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
};

function ModuleEditorModal(props) {
  const isEdit = Boolean(props.modalDataModule?.uuid);

  const [showMenu, setShowMenu] = useState(true);
  const [showSettings, setShowSettings] = useState(!isEdit);
  const [selectedSectionIndex, setSelectedSectionIndex] = useState(0);
  const [selectedSectionUUID, setSelectedSectionUUID] = useState();
  const [hoveredSectionIndex, setHoveredSectionIndex] = useState(null);
  const [isLoadingSection, setIsLoadingSection] = useState(false);

  const [openSectionInputsModal, setOpenSectionInputsModal] = useState(false);
  const [isCreatingNewSection, setIsCreatingNewSection] = useState(false);

  const [addSectionAnchor, setAddSectionAnchor] = useState(null);
  const [openAddExistingDialog, setOpenAddExistingDialog] = useState(false);
  const [pendingExistingNames, setPendingExistingNames] = useState([]);
  const [addExistingInput, setAddExistingInput] = useState("");

  const [openDeleteWarning, setOpenDeleteWarning] = useState(false);
  const [openPreview, setOpenPreview] = useState(false);
  const [previewDevice, setPreviewDevice] = useState("desktop");

  // Snapshot the module on open so Cancel can revert local edits.
  const originalModuleRef = useRef(null);

  // Token to ignore stale section fetches. If the user clicks rapidly
  // between sections, the older API response can resolve last and
  // overwrite modalDataSection with stale content; the token guards
  // against that.
  const sectionFetchIdRef = useRef(0);

  // Mirror current module data + selection so the Undo toast's callback can
  // read them synchronously (the toast closure captures stale removal-time
  // values).
  const modalDataModuleRef = useRef(props.modalDataModule);
  useEffect(() => {
    modalDataModuleRef.current = props.modalDataModule;
  }, [props.modalDataModule]);
  const selectedSectionIndexRef = useRef(selectedSectionIndex);
  useEffect(() => {
    selectedSectionIndexRef.current = selectedSectionIndex;
  }, [selectedSectionIndex]);

  // Pending Undo-toast ids, dismissed whenever the editor closes (any path). A
  // toast left over after close is stale — and worse, if a different module is
  // opened next, its undo callback (which keys off the current module ref + an
  // index) could splice the old section into the wrong module.
  const undoToastIdsRef = useRef([]);
  useEffect(() => {
    if (!props.open) {
      undoToastIdsRef.current.forEach((id) => toast.dismiss(id));
      undoToastIdsRef.current = [];
    }
  }, [props.open]);

  const moduleDistinctCategories = useMemo(() => {
    const categories = props.modulesData?.map((m) => m.category);
    return [...new Set(categories)].filter(Boolean);
  }, [props.modulesData]);

  const [showCreateCategory, setShowCreateCategory] = useState(false);

  useEffect(() => {
    if (props.open) {
      originalModuleRef.current = props.modalDataModule;
      setShowCreateCategory(moduleDistinctCategories.length === 0);
      setSelectedSectionIndex(0);
      // Settings panel: open by default for new modules (where the user
      // is likely to fill them in), closed by default for edits.
      setShowSettings(!isEdit);
      const firstSection = props.modalDataModule?.sections?.[0];
      if (firstSection) {
        setSelectedSectionUUID(firstSection.uuid);
      } else {
        setSelectedSectionUUID(undefined);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open]);

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  // BlockNote schema for read-only section preview

  const quizzesData = props.quizzesData;
  const homeCompany = props.homeCompany;
  const previewLanguage = props.language;

  // Memoized so the per-section <SectionPreview> editor gets a stable schema
  // for its lifetime.
  const schema = useMemo(
    () => buildPreviewSchema({ homeCompany, quizzesData, previewLanguage }),
    [homeCompany, quizzesData, previewLanguage]
  );

  // The section preview is now a self-contained, keyed <SectionPreview> that
  // mounts a fresh editor per section (see render). There is no longer a shared
  // preview editor for the parent to clear, so removeAllBlocks is a no-op kept
  // only so existing callers and child props don't need to change.
  const removeAllBlocks = () => {};

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  // Section detail fetching

  const fetchSectionDetail = async (showLoading, learning_section_uuid) => {
    setIsLoadingSection(showLoading);
    const myFetchId = ++sectionFetchIdRef.current;
    const api_params = {
      company_id: props.homeCompany,
      learning_section_uuid: learning_section_uuid,
    };
    try {
      const res = await getRightworkAPI(
        API_get_learning_section_detail,
        api_params
      );

      // A newer fetch has started since this one was kicked off; abandon
      // its result so it can't overwrite the newer section's data.
      if (sectionFetchIdRef.current !== myFetchId) return undefined;

      // Map signed URL → file UUID for BlockNote media blocks.
      let mapSignedUrlToFileUuid =
        localStorage.getItem("mapSignedUrlToFileUuid") || "{}";
      mapSignedUrlToFileUuid = JSON.parse(mapSignedUrlToFileUuid);
      const rawBlocksAsJson = res.data.redux?.payload?.learning_section.as_json;
      rawBlocksAsJson.forEach((block) => {
        const validTypes = ["image", "video", "audio", "file", "pdf"];
        if (!validTypes.includes(block.type)) return;
        mapSignedUrlToFileUuid[block.props.url] = block.props.file_uuid;
      });
      localStorage.setItem(
        "mapSignedUrlToFileUuid",
        JSON.stringify(mapSignedUrlToFileUuid)
      );

      if (res.status === 200) {
        const sectionData = res.data.redux?.payload?.learning_section;
        props.setModalDataSection(sectionData);
        setIsLoadingSection(false);
        return sectionData;
      }
    } catch (error) {
      // Only clear the loading flag if this is still the latest fetch.
      // Otherwise a newer fetch is in-flight and owns the flag.
      if (sectionFetchIdRef.current === myFetchId) {
        setIsLoadingSection(false);
      }
      console.error("fetchSectionDetail failed", error);
    }
  };

  // Initial section load: fetch the first section's detail on open. The keyed
  // <SectionPreview> in render seeds itself from the fetched blocks.
  useEffect(() => {
    if (props.open && props.modalDataModule?.sections?.length > 0) {
      const firstUuid = props.modalDataModule.sections[0].uuid;
      fetchSectionDetail(true, firstUuid);
      setSelectedSectionIndex(0);
      setSelectedSectionUUID(firstUuid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open]);

  // No manual block-loading effect anymore: <SectionPreview> (rendered below,
  // keyed by the selected section uuid) mounts a fresh editor seeded with the
  // fetched section's blocks. This replaced the old reused-editor load that
  // raced editor.document and threw "Block with ID … not found".

  const selectSection = (index, uuid) => {
    if (isLoadingSection) return;
    setSelectedSectionIndex(index);
    setSelectedSectionUUID(uuid);
    removeAllBlocks();
    fetchSectionDetail(true, uuid);
  };

  // Unlike selectSection, this doesn't wait out a section that's still loading: the
  // editor is about to open, so it has to load the section that was clicked. The
  // fetch token drops the older response.
  const handleEditSection = (index, uuid) => {
    setSelectedSectionIndex(index);
    setSelectedSectionUUID(uuid);
    removeAllBlocks();
    fetchSectionDetail(true, uuid);
    setIsCreatingNewSection(false);
    setOpenSectionInputsModal(true);
  };

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  // Module-level field handlers

  const handleNameChange = (event) => {
    props.setModalDataModule((prev) => ({ ...prev, name: event.target.value }));
  };

  const handleDescriptionChange = (event) => {
    props.setModalDataModule((prev) => ({
      ...prev,
      description: event.target.value,
    }));
  };

  const handleColorSelection = (color) => {
    props.setModalDataModule((prev) => ({ ...prev, color }));
  };

  const handleCategoryChange = (option) => {
    props.setModalDataModule((prev) => ({ ...prev, category: option }));
  };

  const handleCategoryTextChange = (event) => {
    props.setModalDataModule((prev) => ({
      ...prev,
      category: event.target.value,
    }));
  };

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  // Section list mutations (queued locally; persisted on Save)

  const handleOnDragEnd = (result) => {
    if (!result.destination) return;
    const reordered = Array.from(props.modalDataModule?.sections || []);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);
    props.setModalDataModule((prev) => ({ ...prev, sections: reordered }));

    // Keep selected section pointing at the same logical row after a drag.
    const sel = selectedSectionIndex;
    if (sel === result.source.index) {
      setSelectedSectionIndex(result.destination.index);
    } else if (
      result.source.index < sel &&
      result.destination.index >= sel
    ) {
      setSelectedSectionIndex(sel - 1);
    } else if (
      result.source.index > sel &&
      result.destination.index <= sel
    ) {
      setSelectedSectionIndex(sel + 1);
    }
  };

  // Re-insert a section removed via handleRemoveSectionFromModule at its
  // original index. Removal here is local (staged until Save), so the undo is
  // a local splice — no re-post needed.
  const undoRemoveSectionFromModule = (index, removedSection) => {
    const currentSections = modalDataModuleRef.current?.sections || [];
    // Already back (e.g. re-added via "Add existing section") — skip. A
    // duplicate section UUID makes post_learning_module reject the save (its
    // uuid__in returns one row for two requested entries), leaving the module
    // unsavable until the dupe is removed by hand.
    if (currentSections.some((s) => s?.uuid && s.uuid === removedSection?.uuid)) {
      return;
    }
    // Clamp: the list may have shrunk since removal.
    const insertAt = Math.min(index, currentSections.length);

    props.setModalDataModule((prev) => {
      const restored = [...(prev.sections || [])];
      restored.splice(insertAt, 0, removedSection);
      return { ...prev, sections: restored };
    });

    // Reverse the selection move handleRemoveSectionFromModule made, using the
    // CURRENT selection (the user may have navigated during the toast window).
    const currentSel = selectedSectionIndexRef.current;
    if (insertAt === currentSel) {
      // The restored section now occupies the selected slot, but the preview
      // still shows the fallback section — load the restored one so the
      // highlighted row and the content pane agree.
      setSelectedSectionUUID(removedSection?.uuid);
      removeAllBlocks();
      if (removedSection?.uuid) fetchSectionDetail(true, removedSection.uuid);
    } else if (insertAt < currentSel) {
      // Insertion pushed the selected section down one — keep highlight on it.
      setSelectedSectionIndex(currentSel + 1);
    }
  };

  const handleRemoveSectionFromModule = (index) => {
    const sections = props.modalDataModule?.sections || [];
    // Capture before filtering so the undo toast can restore it in place.
    const removedSection = sections[index];
    const updated = sections.filter((_, i) => i !== index);
    props.setModalDataModule((prev) => ({ ...prev, sections: updated }));

    if (removedSection) {
      const toastId = showUndoToast("Section removed from module", () =>
        undoRemoveSectionFromModule(index, removedSection)
      );
      undoToastIdsRef.current.push(toastId);
    }

    // Adjust selection. If we removed the selected row, fall back to the
    // first row (or clear if list is empty).
    if (updated.length === 0) {
      setSelectedSectionIndex(0);
      setSelectedSectionUUID(undefined);
      removeAllBlocks();
    } else if (index === selectedSectionIndex) {
      const newIdx = Math.min(selectedSectionIndex, updated.length - 1);
      const newUuid = updated[newIdx]?.uuid;
      setSelectedSectionIndex(newIdx);
      setSelectedSectionUUID(newUuid);
      removeAllBlocks();
      if (newUuid) fetchSectionDetail(true, newUuid);
    } else if (index < selectedSectionIndex) {
      setSelectedSectionIndex(selectedSectionIndex - 1);
    }
  };

  const handleAddExistingSectionsConfirm = () => {
    const newSections = pendingExistingNames
      .map((name) => props.sectionsData?.find((s) => s.name === name))
      .filter(Boolean);

    const existingUuids = new Set(
      (props.modalDataModule?.sections || []).map((s) => s.uuid)
    );
    const toAdd = newSections.filter((s) => !existingUuids.has(s.uuid));

    if (toAdd.length === 0) {
      setOpenAddExistingDialog(false);
      setPendingExistingNames([]);
      return;
    }

    props.setModalDataModule((prev) => ({
      ...prev,
      sections: [...(prev.sections || []), ...toAdd],
    }));
    setOpenAddExistingDialog(false);
    setPendingExistingNames([]);
    setAddExistingInput("");
  };

  const handleNewSectionCreated = async (newSection) => {
    const updatedModule = {
      ...props.modalDataModule,
      sections: [...(props.modalDataModule.sections || []), newSection],
    };
    props.setModalDataModule(updatedModule);
    await props.post_learning_module(updatedModule);

    const freshModule = props.modulesData?.find(
      (m) => m.uuid === updatedModule.uuid
    );
    const hasNewSection = freshModule?.sections?.some(
      (s) => s.uuid === newSection.uuid
    );
    if (freshModule && hasNewSection) {
      props.setModalDataModule(freshModule);
    }

    const newSectionIndex = updatedModule.sections.length - 1;
    setSelectedSectionIndex(newSectionIndex);
    setSelectedSectionUUID(newSection.uuid);
    removeAllBlocks();
    await fetchSectionDetail(true, newSection.uuid);
    setIsCreatingNewSection(false);
  };

  const refresh_section = async () => {
    if (!selectedSectionUUID) return;
    const updated = await fetchSectionDetail(true, selectedSectionUUID);
    if (updated) {
      const updatedSections = (props.modalDataModule?.sections || []).map(
        (section) =>
          section.uuid === selectedSectionUUID
            ? {
                ...section,
                name: updated.name,
                category: updated.category,
                test_out_quiz: updated.test_out_quiz,
              }
            : section
      );
      props.setModalDataModule((prev) => ({
        ...prev,
        sections: updatedSections,
      }));
    }
  };

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  // Save / Cancel

  const handleSave = async () => {
    const savedModule = await props.post_learning_module();
    if (props.onSaveSuccess && !isEdit && savedModule) {
      props.onSaveSuccess(savedModule);
    }
    // Persist the "make directly assignable" setting on Save (only when it
    // changed) — like every other setting in the editor.
    const desiredAssignable = !!props.modalDataModule?.is_directly_assignable;
    const originalAssignable = !!originalModuleRef.current?.is_directly_assignable;
    if (savedModule?.uuid && desiredAssignable !== originalAssignable) {
      await setDirectAssignability({
        companyId: props.homeCompany,
        assetType: "module",
        assetUuid: savedModule.uuid,
        isAssignable: desiredAssignable,
      });
    }
    // Clear BlockNote on close. The wrapping component stays mounted
    // across open/close (only the MUI Modal portal toggles), so the
    // editor instance survives. Without this, blocks from this session
    // remain in the document and the next open would render its first
    // section's content *in addition to* the new section — duplicated
    // content in the viewer. Mirrors SectionInputsModal2's pattern.
    removeAllBlocks();
    props.handleClose();
  };

  const handleClose = () => {
    // Revert any unsaved local edits (name, color, category, section
    // order, add/remove) to the snapshot taken when the modal opened.
    if (originalModuleRef.current) {
      props.setModalDataModule(originalModuleRef.current);
    }
    setPendingExistingNames([]);
    setAddExistingInput("");
    // See handleSave for why this clear is required.
    removeAllBlocks();
    props.handleClose();
  };

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  // Render helpers

  const sections = props.modalDataModule?.sections || [];
  const sectionCount = sections.length;
  const hasSections = sectionCount > 0;

  return (
    <>
      <Modal
        open={props.open}
        aria-labelledby="module-editor-modal"
      >
        <Box sx={modalStyle}>
          {/* = = = Header bar = = = */}
          <div className="flex items-center justify-between border-b border-slate-200 bg-white px-4 py-3 shrink-0">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div
                className={`w-9 h-9 rounded-lg ${
                  props.modalDataModule?.color || "bg-violet-600"
                } flex items-center justify-center shrink-0`}
              >
                <span className="text-white text-xs font-semibold">M</span>
              </div>
              <div className="min-w-0 flex-1">
                <TextField
                  placeholder={isEdit ? "Module name" : "Untitled module"}
                  value={props.modalDataModule?.name || ""}
                  onChange={handleNameChange}
                  variant="standard"
                  fullWidth
                  InputProps={{
                    disableUnderline: true,
                    sx: {
                      fontSize: "1.25rem",
                      fontWeight: 600,
                      color: "#0f172a",
                    },
                  }}
                />
                <input
                  type="text"
                  value={props.modalDataModule?.description || ""}
                  onChange={handleDescriptionChange}
                  placeholder="Add a short description"
                  className="mt-0.5 block w-full bg-transparent border-none px-0 text-sm text-slate-500 placeholder:text-slate-400 focus:outline-none focus:ring-0"
                />
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <LearningPreviewMenuButton
                variant="button"
                onSelect={(device) => {
                  setPreviewDevice(device);
                  setOpenPreview(true);
                }}
              />
              <button
                onClick={handleSave}
                className="px-4 py-2 bg-violet-700 hover:bg-violet-800 text-white rounded-lg text-sm font-semibold transition-colors"
              >
                Save Module
              </button>
              <button
                onClick={handleClose}
                aria-label="Close"
                className="w-9 h-9 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-500 hover:text-slate-700 transition-colors"
              >
                <CloseIcon />
              </button>
            </div>
          </div>

          {/* = = = Body: 3 panes = = = */}
          <div className="flex-1 flex overflow-hidden bg-white">
            {/* - - - Left: sections sidebar - - - */}
            {showMenu && (
              <div className="w-[280px] shrink-0 border-r border-slate-200 bg-slate-50 flex flex-col overflow-hidden">
                <div className="h-14 px-4 border-b border-slate-200 flex items-center justify-between shrink-0">
                  <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
                    Sections ({sectionCount})
                  </p>
                  <IconButton
                    size="small"
                    onClick={() => setShowMenu(false)}
                    title="Hide sidebar"
                  >
                    <MenuIcon fontSize="small" />
                  </IconButton>
                </div>

                <div className="flex-1 overflow-auto">
                  {hasSections ? (
                    <DragDropContext onDragEnd={handleOnDragEnd}>
                      <Droppable droppableId="module-sections">
                        {(provided) => (
                          <div
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                          >
                            {sections.map((section, index) => (
                              <Draggable
                                key={section.uuid || section.name}
                                draggableId={section.uuid || section.name}
                                index={index}
                              >
                                {(dragProvided, snapshot) => (
                                  <div
                                    ref={dragProvided.innerRef}
                                    {...dragProvided.draggableProps}
                                    style={{
                                      ...dragProvided.draggableProps.style,
                                      left: "auto !important",
                                      top: "auto !important",
                                    }}
                                    onMouseEnter={() =>
                                      setHoveredSectionIndex(index)
                                    }
                                    onMouseLeave={() =>
                                      setHoveredSectionIndex(null)
                                    }
                                    onClick={() =>
                                      selectSection(index, section.uuid)
                                    }
                                    className={`group flex items-center gap-2 pl-2 pr-3 py-3 border-b border-slate-200 cursor-pointer transition-colors relative ${
                                      snapshot.isDragging
                                        ? "bg-violet-50 shadow-md"
                                        : selectedSectionIndex === index
                                        ? "bg-violet-50"
                                        : "bg-white hover:bg-slate-100"
                                    }`}
                                  >
                                    {selectedSectionIndex === index && (
                                      <div className="h-full w-1 bg-violet-700 absolute left-0 top-0"></div>
                                    )}
                                    <div
                                      className="text-slate-400 cursor-grab hover:text-slate-600"
                                      {...dragProvided.dragHandleProps}
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <DragIndicatorIcon fontSize="small" />
                                    </div>
                                    <p className="text-xxs text-slate-500 shrink-0">
                                      {index + 1}.
                                    </p>
                                    <Tooltip title={section.name || ""} placement="right" arrow>
                                      <div className="flex-1 min-w-0">
                                        <p className="text-xs text-slate-900 truncate">
                                          {section.name}
                                        </p>
                                        {section.test_out_quiz && (
                                          <p className="text-xxs text-violet-700 truncate">
                                            Skips if passed: {section.test_out_quiz.name}
                                          </p>
                                        )}
                                      </div>
                                    </Tooltip>
                                    <div
                                      className={`absolute right-1.5 top-1/2 -translate-y-1/2 flex items-center gap-0.5 rounded-lg border border-slate-200 bg-white px-1 py-0.5 shadow-sm transition-opacity ${
                                        hoveredSectionIndex === index
                                          ? "opacity-100"
                                          : "opacity-0 pointer-events-none"
                                      }`}
                                    >
                                      <IconButton
                                        size="small"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleEditSection(index, section.uuid);
                                        }}
                                        sx={{
                                          color: "#6d28d9",
                                          padding: "3px",
                                          "&:hover": {
                                            backgroundColor: "#f5f3ff",
                                          },
                                        }}
                                        title="Edit section content"
                                      >
                                        <EditIcon style={{ fontSize: 15 }} />
                                      </IconButton>
                                      <IconButton
                                        size="small"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleRemoveSectionFromModule(index);
                                        }}
                                        sx={{
                                          color: "#94a3b8",
                                          padding: "3px",
                                          "&:hover": {
                                            backgroundColor: "#fef2f2",
                                            color: "#dc2626",
                                          },
                                        }}
                                        title="Remove from module"
                                      >
                                        <CloseIcon style={{ fontSize: 15 }} />
                                      </IconButton>
                                    </div>
                                  </div>
                                )}
                              </Draggable>
                            ))}
                            {provided.placeholder}
                          </div>
                        )}
                      </Droppable>
                    </DragDropContext>
                  ) : (
                    <div className="px-4 py-12 text-center">
                      <p className="text-xs text-slate-500">
                        No sections yet. Add one below.
                      </p>
                    </div>
                  )}
                </div>

                <div className="h-14 border-t border-slate-200 px-3 bg-white flex items-center shrink-0">
                  <button
                    onClick={(e) => setAddSectionAnchor(e.currentTarget)}
                    className="flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-violet-300 bg-violet-50 px-3 py-2 text-xs font-semibold text-violet-700 transition-colors hover:border-violet-500 hover:bg-violet-100 hover:text-violet-800"
                  >
                    <span className="flex h-4 w-4 items-center justify-center rounded-full bg-white text-violet-700 shadow-sm">
                      <AddIcon style={{ fontSize: "14px" }} />
                    </span>
                    <span>Add Section</span>
                  </button>
                  <Menu
                    anchorEl={addSectionAnchor}
                    open={Boolean(addSectionAnchor)}
                    onClose={() => setAddSectionAnchor(null)}
                    anchorOrigin={{
                      vertical: "top",
                      horizontal: "center",
                    }}
                    transformOrigin={{
                      vertical: "bottom",
                      horizontal: "center",
                    }}
                  >
                    {isEdit ? (
                      <MenuItem
                        onClick={() => {
                          setAddSectionAnchor(null);
                          props.setModalDataSection(props.default_data_section);
                          setIsCreatingNewSection(true);
                          setOpenSectionInputsModal(true);
                        }}
                      >
                        <AddIcon fontSize="small" className="mr-2" />
                        Create new section
                      </MenuItem>
                    ) : (
                      // New section needs a parent module UUID to attach to,
                      // which only exists after first save. Render disabled
                      // with a tooltip explaining the prerequisite instead
                      // of hiding the option (which left users confused).
                      // MUI requires a non-disabled wrapper for tooltips
                      // over disabled items, hence the <span>.
                      <Tooltip
                        title="Save this module first to create sections here"
                        placement="right"
                      >
                        <span>
                          <MenuItem disabled>
                            <AddIcon fontSize="small" className="mr-2" />
                            Create new section
                          </MenuItem>
                        </span>
                      </Tooltip>
                    )}
                    <MenuItem
                      onClick={() => {
                        setAddSectionAnchor(null);
                        setPendingExistingNames([]);
                        setAddExistingInput("");
                        setOpenAddExistingDialog(true);
                      }}
                    >
                      <CategoryIcon fontSize="small" className="mr-2" />
                      Add existing section
                    </MenuItem>
                  </Menu>
                </div>
              </div>
            )}

            {/* - - - Center: section preview - - - */}
            <div className="flex-1 flex flex-col overflow-hidden bg-white">
              <div className="h-14 border-b border-slate-200 px-6 flex items-center justify-between bg-white shrink-0">
                {!showMenu && (
                  <IconButton
                    size="small"
                    onClick={() => setShowMenu(true)}
                    title="Show sidebar"
                  >
                    <MenuIcon fontSize="small" />
                  </IconButton>
                )}
                <p className="text-sm text-slate-500 mx-auto">
                  {hasSections
                    ? `Section ${selectedSectionIndex + 1} of ${sectionCount}`
                    : "No section selected"}
                </p>
                {!showSettings ? (
                  <IconButton
                    size="small"
                    onClick={() => setShowSettings(true)}
                    title="Show settings"
                  >
                    <TuneIcon fontSize="small" />
                  </IconButton>
                ) : (
                  <div className="w-9" />
                )}
              </div>

              <div className="flex-1 overflow-auto bg-white pt-8">
                {hasSections ? (
                  isLoadingSection ? (
                    <div className="h-full w-full flex items-center justify-center">
                      <LoadSpinner />
                    </div>
                  ) : (
                    // Keyed on the loaded section's uuid so a fresh editor
                    // mounts per section, seeded via initialContent (key and
                    // content come from the same object, so they can't drift) —
                    // no shared editor whose document desyncs across
                    // remove/replace cycles (the old "Block with ID … not
                    // found" / blank-pane bug).
                    <>
                      <TestOutSkipsNote
                        blocks={props.modalDataSection?.as_json}
                        sections={sections}
                      />
                      <SectionPreview
                        key={props.modalDataSection?.uuid}
                        schema={schema}
                        asJson={props.modalDataSection?.as_json}
                      />
                    </>
                  )
                ) : (
                  <div className="h-full w-full flex items-center justify-center text-center px-8">
                    <div>
                      <p className="text-slate-500 text-sm mb-3">
                        This module has no sections yet.
                      </p>
                      <p className="text-slate-400 text-xs">
                        Use the sidebar to add or create a section.
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {hasSections && (
                <div className="h-14 border-t border-slate-200 px-6 flex items-center justify-between bg-white shrink-0">
                  <button
                    disabled={selectedSectionIndex === 0}
                    onClick={() =>
                      selectSection(
                        selectedSectionIndex - 1,
                        sections[selectedSectionIndex - 1]?.uuid
                      )
                    }
                    className="py-1.5 px-3 rounded text-sm flex items-center gap-1 transition-colors disabled:opacity-40 disabled:cursor-not-allowed text-slate-700 hover:bg-slate-100"
                  >
                    <ChevronLeftIcon fontSize="small" />
                    Previous
                  </button>
                  <p className="text-xs text-slate-500">
                    {sections[selectedSectionIndex]?.name}
                  </p>
                  <button
                    disabled={selectedSectionIndex >= sectionCount - 1}
                    onClick={() =>
                      selectSection(
                        selectedSectionIndex + 1,
                        sections[selectedSectionIndex + 1]?.uuid
                      )
                    }
                    className="py-1.5 px-3 rounded text-sm flex items-center gap-1 transition-colors disabled:opacity-40 disabled:cursor-not-allowed text-slate-700 hover:bg-slate-100"
                  >
                    Next
                    <ChevronRightIcon fontSize="small" />
                  </button>
                </div>
              )}
            </div>

            {/* - - - Right: settings - - - */}
            {showSettings && (
            <div className="w-[320px] shrink-0 border-l border-slate-200 bg-white overflow-auto">
              <div className="h-14 px-5 border-b border-slate-200 flex items-center justify-between shrink-0">
                <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
                  Module Settings
                </p>
                <IconButton
                  size="small"
                  onClick={() => setShowSettings(false)}
                  title="Hide settings"
                >
                  <ChevronRightIcon fontSize="small" />
                </IconButton>
              </div>

              <div className="px-5 py-5 space-y-6">
                {/* Category */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-slate-700">
                      Category
                    </label>
                    <button
                      className="text-xs text-violet-600 hover:text-violet-700 font-medium"
                      onClick={() => {
                        if (
                          showCreateCategory &&
                          moduleDistinctCategories.length > 0
                        ) {
                          setShowCreateCategory(false);
                        } else {
                          setShowCreateCategory(true);
                          props.setModalDataModule((prev) => ({
                            ...prev,
                            category: "",
                          }));
                        }
                      }}
                    >
                      {showCreateCategory
                        ? moduleDistinctCategories.length > 0
                          ? "Back to Options"
                          : ""
                        : "Create New"}
                    </button>
                  </div>
                  {showCreateCategory ? (
                    <TextField
                      placeholder="Create a new category"
                      fullWidth
                      variant="outlined"
                      size="small"
                      name="category"
                      value={props.modalDataModule?.category || ""}
                      onChange={handleCategoryTextChange}
                      InputProps={{
                        sx: {
                          backgroundColor: "white",
                          borderRadius: "8px",
                          height: "42px",
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#6d28d9",
                          },
                        },
                      }}
                    />
                  ) : (
                    <CustomCategorySelect
                      options={moduleDistinctCategories}
                      value={props.modalDataModule?.category}
                      onChange={handleCategoryChange}
                      name="Category"
                      placeholder="Select category"
                    />
                  )}
                </div>

                {/* Make directly assignable */}
                <DirectAssignabilityToggle
                  value={props.modalDataModule?.is_directly_assignable}
                  onChange={(val) =>
                    props.setModalDataModule((prev) => ({
                      ...prev,
                      is_directly_assignable: val,
                    }))
                  }
                />

                {/* Color */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <PaletteIcon
                      className="text-slate-500"
                      style={{ fontSize: "18px" }}
                    />
                    <label className="block text-sm font-medium text-slate-700">
                      Color
                    </label>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {COLOR_OPTIONS.map((opt) => (
                      <div
                        key={opt.value}
                        className={`p-2 border rounded-lg cursor-pointer transition-all hover:border-slate-400 ${
                          props.modalDataModule?.color === opt.value
                            ? "border-violet-500 bg-violet-50"
                            : "border-slate-200"
                        }`}
                        onClick={() => handleColorSelection(opt.value)}
                      >
                        <div className="flex items-center gap-2">
                          <div
                            className={`w-5 h-5 rounded-full ${opt.value} flex items-center justify-center`}
                          >
                            {props.modalDataModule?.color === opt.value && (
                              <CheckIcon
                                className="text-white"
                                style={{ fontSize: "12px" }}
                              />
                            )}
                          </div>
                          <span className="text-xs text-slate-700">
                            {opt.name}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Delete */}
                {isEdit && (
                  <div className="pt-4 border-t border-slate-200">
                    <button
                      onClick={() => setOpenDeleteWarning(true)}
                      className="w-full py-2 px-3 bg-red-50 hover:bg-red-100 text-red-700 rounded-lg text-sm flex items-center justify-center gap-2 font-medium transition-colors"
                    >
                      <DeleteIcon fontSize="small" />
                      Delete Module
                    </button>
                  </div>
                )}
              </div>
            </div>
            )}
          </div>
        </Box>
      </Modal>

      {/* = = = Add existing sections dialog = = = */}
      <Dialog
        open={openAddExistingDialog}
        onClose={() => setOpenAddExistingDialog(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Add existing sections</DialogTitle>
        <DialogContent>
          <Autocomplete
            multiple
            fullWidth
            value={pendingExistingNames}
            onChange={(_event, newValue) => setPendingExistingNames(newValue)}
            inputValue={addExistingInput}
            onInputChange={(_event, newInputValue) =>
              setAddExistingInput(newInputValue)
            }
            disableClearable
            disableCloseOnSelect
            options={(props.sectionsData || []).map((s) => s.name)}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                size="small"
                placeholder="Select sections to add"
                sx={{
                  mt: 1,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px !important",
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#6d28d9",
                    },
                  },
                }}
              />
            )}
            renderOption={(optionProps, option, { selected }) => {
              const { key, ...otherProps } = optionProps;
              return (
                <li key={key} {...otherProps}>
                  <Checkbox
                    icon={checkboxIcon}
                    checkedIcon={checkboxCheckedIcon}
                    style={{ marginRight: 8 }}
                    checked={selected}
                    sx={{
                      color: "#6d28d9",
                      "&.Mui-checked": { color: "#6d28d9" },
                    }}
                  />
                  {option}
                </li>
              );
            }}
            renderTags={() => null}
          />
          {pendingExistingNames.length > 0 && (
            <p className="text-xs text-slate-500 mt-3">
              {pendingExistingNames.length}{" "}
              {pendingExistingNames.length === 1 ? "section" : "sections"}{" "}
              selected
            </p>
          )}
        </DialogContent>
        <DialogActions>
          <button
            onClick={() => setOpenAddExistingDialog(false)}
            className="px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 rounded-lg"
          >
            Cancel
          </button>
          <button
            onClick={handleAddExistingSectionsConfirm}
            disabled={pendingExistingNames.length === 0}
            className="px-4 py-2 text-sm bg-violet-700 hover:bg-violet-800 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Add{" "}
            {pendingExistingNames.length > 0
              ? `(${pendingExistingNames.length})`
              : ""}
          </button>
        </DialogActions>
      </Dialog>

      {/* = = = Section content drill-in = = = */}
      <SectionInputsModal2
        open={openSectionInputsModal}
        handleClose={() => {
          setOpenSectionInputsModal(false);
          setIsCreatingNewSection(false);
        }}
        modalEdit={!isCreatingNewSection}
        isLoading={isLoadingSection}
        modalDataSection={props.modalDataSection}
        setModalDataSection={props.setModalDataSection}
        post_learning_section={props.post_learning_section}
        homeCompany={props.homeCompany}
        homeStore={props.homeStore}
        delete_learning_section={props.delete_learning_section}
        modalDataQuiz={props.modalDataQuiz}
        setModalDataQuiz={props.setModalDataQuiz}
        default_data_quiz={props.default_data_quiz}
        post_learning_quiz={props.post_learning_quiz}
        quizzesData={props.quizzesData}
        type="Preview"
        removeAllBlocks={removeAllBlocks}
        refresh_section={refresh_section}
        onSaveSuccess={isCreatingNewSection ? handleNewSectionCreated : null}
        parentModuleUuid={props.modalDataModule?.uuid}
        hideCategory={true}
        sectionsData={props.sectionsData}
        modulesData={props.modulesData}
      />

      {/* = = = Delete warning = = = */}
      <DeleteWarningModal
        open={openDeleteWarning}
        handleClose={() => setOpenDeleteWarning(false)}
        type="module"
        delete_function={props.delete_learning_module}
        handleCloseInputsModal={() => {
          removeAllBlocks();
          props.handleClose();
        }}
      />
      <LearningContentPreviewModal
        open={openPreview}
        onClose={() => setOpenPreview(false)}
        previewType="module"
        item={props.modalDataModule}
        initialDevice={previewDevice}
        homeCompany={props.homeCompany}
        quizzesData={props.quizzesData}
      />
    </>
  );
}

export default ModuleEditorModal;
